import { Link } from "react-router-dom";
import { IMG, PURE_SOULS_URL } from "@/lib/site";
import SectionHeader from "@/components/site/SectionHeader";
import Reveal from "@/components/site/Reveal";
import Seo from "@/components/site/Seo";
import { PAGE_SEO, breadcrumbLd, processHowToLd } from "@/lib/seo";

const steps = [
  {
    n: "01",
    title: "Respectful Transfer",
    body: "Your pet is respectfully received into the care of the provider. The journey begins with calm, considered handling and the first step in tracking your pet's individual identity.",
    image: IMG.heroFog,
  },
  {
    n: "02",
    title: "Identification & Tracking",
    body: "Each pet is given a unique identifier that remains with them through every stage of the process, ensuring that the remains returned to your family are unmistakably your own pet's.",
    image: IMG.texSage,
  },
  {
    n: "03",
    title: "Gentle Water Cremation",
    body: "Your pet is placed inside a stainless steel vessel and gently submerged in warm water combined with a precise, measured amount of alkaline solution. Over several hours, warmth and gentle chemistry accelerate a natural process.",
    image: IMG.texWater,
  },
  {
    n: "04",
    title: "Careful Processing",
    body: "The remaining mineral matter, primarily calcium and phosphorus, is rinsed, gently dried, and processed into a soft, fine, light-coloured powder.",
    image: IMG.heroClouds,
  },
  {
    n: "05",
    title: "Return To Family",
    body: "The remains are placed into your chosen memorial option and respectfully returned to your family, typically within one to two weeks.",
    image: IMG.dogGrass,
  },
];

export default function TheProcess() {
  return (
    <div data-testid="process-page">
      <Seo
        {...PAGE_SEO.process}
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "The Process", path: "/the-process" },
          ]),
          processHowToLd(steps),
        ]}
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={IMG.heroClouds} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#F9F9F6]/85" />
        </div>
        <div className="container-editorial pt-20 pb-16">
          <div className="overline mb-5">Step By Step</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] text-forest-900 max-w-4xl">
            The process, <span className="italic text-sage-600">unhurried, quiet, considered.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg sm:text-xl text-forest-700 leading-[1.65]">
            A calm walk through what actually happens during water cremation,
            from the moment your pet is received to the moment they return home.
          </p>
        </div>
      </section>

      <section className="container-editorial section-padding">
        <div className="space-y-24 sm:space-y-32">
          {steps.map((s, i) => (
            <Reveal key={s.n} y={30}>
            <article
              className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""
              }`}
              data-testid={`process-detail-step-${s.n}`}
            >
              <div className="lg:col-span-6">
                <div className="rounded-3xl overflow-hidden aspect-[4/3] shadow-card">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="lg:col-span-6">
                <div className="font-serif text-7xl lg:text-8xl text-sage-500/70 leading-none mb-4">
                  {s.n}
                </div>
                <h2 className="font-serif text-4xl sm:text-5xl text-forest-900 tracking-tight leading-tight mb-5">
                  {s.title}
                </h2>
                <p className="text-forest-700 text-lg leading-[1.85] max-w-xl">
                  {s.body}
                </p>
              </div>
            </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-sand-100 section-padding">
        <div className="container-editorial max-w-3xl">
          <SectionHeader
            overline="A Quiet Note"
            title="Take the time you need."
          />
          <p className="text-forest-700 text-lg leading-[1.85] mb-8">
            There is no rush. Speak with providers, ask the questions that
            matter to your family, and choose what feels right. The decisions
            you make should reflect who your pet was and who you are.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/providers" className="btn-secondary">Find a provider</Link>
            <a
              href={PURE_SOULS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              data-testid="process-pure-souls-cta"
            >
              Visit Pure Souls →
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
